import { RebrandShareCard } from "@/components/growth/RebrandPopup";
import { InviteWaysSection } from "@/components/growth/InviteRules";
import { GatesOverview } from "@/components/growth/InviteLock";
import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Share2, MessageCircle, Users, Loader2, Facebook, Send, ImageIcon } from "lucide-react";
import { ShareImageDialog } from "@/components/BadgeShare";
import { renderMemberPromo } from "@/lib/promoCards";
import { useAuth } from "@/context/AuthContext";
import { imgSrc } from "@/lib/api";
import { toast } from "sonner";
import { api, fmtDate } from "@/lib/api";
import { PageExplain } from "@/components/PageExplain";
import { UserLink } from "@/components/UserBits";
import { InviteBonusHero } from "@/components/InviteBonusHero";
import { InviteTiers, ShareStats, TopInviters } from "@/components/growth/GrowthParts";
import { useGrowth } from "@/lib/growth";
import { getShareUrl } from "@/lib/share";
import { AdTexts } from "@/components/growth/AdTexts";

const inviteSteps = (data) => [
  "Kopiere deinen persönlichen Einladungslink oder teile ihn direkt per WhatsApp, Messenger oder auf Facebook.",
  "Dein Freund registriert sich über den Link (oder gibt deinen Code bei der Registrierung ein).",
  `Sobald das Konto erstellt ist, bekommst du automatisch ${data.points_per_friend} Punkte gutgeschrieben.`,
];

export default function Invite() {
  const [data, setData] = useState(null);
  const [promo, setPromo] = useState(false);
  const { user } = useAuth();
  const g = useGrowth();
  const [short, setShort] = useState(null);
  useEffect(() => { api.get("/referrals").then(({ data }) => setData(data)); getShareUrl({ kind: "invite" }).then(setShort).catch(() => {}); }, []);
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;

  const link = short || `${window.location.origin}/registrieren?ref=${data.referral_code}`;
  const text = `Komm in die itzPlay Community! Posten, spielen, Punkte sammeln & tolle Prämien bekommen 💖 ${link}`;
  const copy = () => { navigator.clipboard?.writeText(link); toast.success("Link kopiert!"); };
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const messenger = () => {
    if (isMobile) { window.location.href = `fb-messenger://share/?link=${encodeURIComponent(link)}`; return; }
    navigator.clipboard?.writeText(text);
    toast.success("Einladung kopiert – füge sie im Messenger bei deinen Freunden ein 💬");
    window.open("https://www.messenger.com/", "_blank", "noopener,noreferrer");
  };
  const fbPost = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`, "fb-share", "width=620,height=560,noopener");
  const share = () => (navigator.share ? navigator.share({ title: "itzPlay", text, url: link }).catch(() => {}) : copy());

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs tracking-wider uppercase font-semibold text-rose-600">Freunde einladen</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Lade deine Freunde ein</h1>
      </div>
      <InviteBonusHero data={data} />
      <RebrandShareCard />
      <GatesOverview />
      <InviteWaysSection />
      {g && <InviteTiers g={g} />}
      <PageExplain testId="invite-explain" steps={inviteSteps(data)} />
      <div className="grid md:grid-cols-5 gap-5">
        <div className="md:col-span-3 min-w-0 bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 space-y-5">
          <div>
            <p className="text-sm text-slate-500">Dein Code</p>
            <p className="font-mono text-2xl sm:text-3xl font-extrabold tracking-widest text-rose-600 break-all" data-testid="referral-code">{data.referral_code}</p>
          </div>
          <div className="flex items-center gap-2 p-2 pl-4 rounded-full bg-rose-50 border border-rose-100">
            <span className="text-sm truncate flex-1 min-w-0 text-slate-600" data-testid="referral-link">{link}</span>
            <button onClick={copy} data-testid="copy-referral-btn" className="shrink-0 inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-rose-500 text-white text-sm font-bold hover:bg-rose-600 transition-colors"><Copy className="w-4 h-4" /> Kopieren</button>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp-btn" className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-emerald-500 text-white font-bold text-sm hover:bg-emerald-600 transition-colors"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            <button onClick={messenger} data-testid="share-messenger-btn" className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-gradient-to-r from-[#00B2FF] via-[#7B61FF] to-[#FF5C87] text-white font-bold text-sm hover:opacity-90 transition-opacity"><Send className="w-4 h-4" /> Messenger</button>
            <button onClick={fbPost} data-testid="share-facebook-btn" className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[#1877F2] text-white font-bold text-sm hover:bg-[#166FE5] transition-colors"><Facebook className="w-4 h-4" /> Auf Facebook posten</button>
            <button onClick={share} data-testid="share-native-btn" className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-slate-900 text-white font-bold text-sm"><Share2 className="w-4 h-4" /> Teilen</button>
          </div>
          <button onClick={() => setPromo(true)} data-testid="member-promo-btn" className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-gradient-to-r from-amber-300 to-pink-400 text-rose-950 font-extrabold text-sm shadow-lg shadow-amber-200 active:scale-95 transition-transform"><ImageIcon className="w-4 h-4" /> Mein persönliches Werbebild</button>
          {promo && user && (
            <ShareImageDialog prefix="member-promo" title="Dein Werbebild" alt="Mein itzPlay Werbebild" fileBase={`itzcouple-einladung-${data.referral_code}`} onClose={() => setPromo(false)}
              description="Dein persönliches Bild mit Profilbild und Einladungscode – teile es in deiner Story, im Status oder per WhatsApp."
              msg={text} deps={[user.user_id, data.referral_code]}
              render={(format) => renderMemberPromo({ user: { ...user, picture: user.picture ? imgSrc(user.picture) : null, referral_code: data.referral_code }, format })} />
          )}
        </div>
        <div className="md:col-span-2 min-w-0 bg-white rounded-3xl p-6 border border-pink-100 flex flex-col items-center justify-center gap-3">
          <div className="p-3 rounded-2xl bg-white border border-pink-100" data-testid="referral-qr"><QRCodeSVG value={link} size={160} fgColor="#E11D48" /></div>
          <p className="text-xs text-slate-500 text-center">QR-Code zum Scannen – perfekt, wenn ihr zusammen seid.</p>
        </div>
      </div>
      <AdTexts link={link} />
      <div className="grid lg:grid-cols-2 gap-5 items-start">
        {g && <ShareStats g={g} />}
        <TopInviters />
      </div>
      <div className="bg-white rounded-3xl p-6 border border-pink-100">
        <h3 className="font-bold flex items-center gap-2"><Users className="w-5 h-5 text-rose-500" /> Deine eingeladenen Freunde ({data.friends.length})</h3>
        <ul className="mt-3 divide-y divide-pink-50" data-testid="referral-friends-list">
          {data.friends.length === 0 && <li className="py-6 text-center text-sm text-slate-400">Noch niemand – teile jetzt deinen Link!</li>}
          {data.friends.map((f) => (
            <li key={`${f.name}-${f.created_at}`} className="py-3 flex items-center justify-between gap-3 text-sm">
              <UserLink id={f.user_id} className="font-medium truncate min-w-0">{f.name}</UserLink>
              <span className="text-xs text-slate-400 shrink-0">{fmtDate(f.created_at)} · <b className="text-emerald-600">+{data.points_per_friend}</b></span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
